import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useAuth } from './AuthContext';

// Extended user interface that includes the _id field
export interface AuthenticatedUser {
  LoggedInUserId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'admin' | 'editor' | 'viewer';
}

interface AuthenticatedUserInfoContextType {
  authenticatedUser: AuthenticatedUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  updateUserInfo: (user: AuthenticatedUser) => void;
}

const AuthenticatedUserInfoContext = createContext<AuthenticatedUserInfoContextType | undefined>(
  undefined
);

export const AuthenticatedUserInfoProvider = ({ children }: { children: ReactNode }) => {
  const { user, token } = useAuth();
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    if (user && token) {
      // Try to get user info from localStorage first
      const storedUserInfo = localStorage.getItem('authenticatedUserInfo');
      if (storedUserInfo) {
        try {
          const parsedUser = JSON.parse(storedUserInfo) as AuthenticatedUser;
          setAuthenticatedUser(parsedUser);
          setLoading(false);
          return;
        } catch (error) {
          console.error('Error parsing stored user info:', error);
        }
      }

      // If we have basic user info, enhance it with proper typing
      if (user) {
        const enhancedUser: AuthenticatedUser = {
          LoggedInUserId: user._id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          role: user.role,
        };

        console.log('Authenticated user details:', enhancedUser);

        console.log('Logged in user token:', token);

        setAuthenticatedUser(enhancedUser);
        localStorage.setItem('authenticatedUserInfo', JSON.stringify(enhancedUser));
      }
    } else {
      // Clear user info if not authenticated
      setAuthenticatedUser(null);
      localStorage.removeItem('authenticatedUserInfo');
    }

    setLoading(false);
  }, [user, token]);

  const updateUserInfo = (user: AuthenticatedUser) => {
    setAuthenticatedUser(user);
    localStorage.setItem('authenticatedUserInfo', JSON.stringify(user));
  };

  const isAuthenticated = !!authenticatedUser && !!token;

  return (
    <AuthenticatedUserInfoContext.Provider
      value={{
        authenticatedUser,
        isAuthenticated,
        loading,
        updateUserInfo,
      }}
    >
      {children}
    </AuthenticatedUserInfoContext.Provider>
  );
};

export const useAuthenticatedUser = () => {
  const context = useContext(AuthenticatedUserInfoContext);
  if (!context) {
    throw new Error('useAuthenticatedUser must be used within an AuthenticatedUserInfoProvider');
  }
  return context;
};
